import { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import type { Event } from '@peles/shared';
import { useCreateEvent } from '../../hooks/useEvents';
import { useCloneTemplateToEvent, useTemplates } from '../../hooks/useTemplates';
import { useDialog } from '../../context/DialogContext';
import { VenueSelect } from '../common/VenueSelect';
import { extractApiErrorMessage } from '../../utils/apiError';
import { EventTypeCards } from './create/EventTypeCards';
import { EventWhenFields } from './create/EventWhenFields';
import { QuickVenueForm } from './create/QuickVenueForm';
import {
  EventTemplatePicker,
  isStarterTemplate,
  suggestedTemplateIds,
} from './create/EventTemplatePicker';
import { EMPTY_WHEN, buildEventWhen, type EventWhen } from './create/eventWhen';

interface CreateEventPanelProps {
  onClose: () => void;
  /** Called with the new event, so the caller can open it. */
  onCreated: (event?: Event) => void;
}

export const CREATE_EVENT_STEPS = [
  { id: 'basics', label: 'Basics' },
  { id: 'venue', label: 'Venue' },
  { id: 'details', label: 'Details' },
] as const;

type StepId = (typeof CREATE_EVENT_STEPS)[number]['id'];

interface FormState {
  name: string;
  eventTypeId: string;
  when: EventWhen;
  venueId: string;
  guestCount: string;
  budget: string;
  notes: string;
}

const initialForm: FormState = {
  name: '',
  eventTypeId: '',
  when: EMPTY_WHEN,
  venueId: '',
  guestCount: '',
  budget: '',
  notes: '',
};

const input =
  'w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function StepIndicator({
  current,
  reachable,
  onSelect,
}: {
  current: number;
  reachable: number;
  onSelect: (index: number) => void;
}) {
  return (
    <ol className="flex items-center gap-2 mb-6" aria-label="Steps">
      {CREATE_EVENT_STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={step.id} className="flex items-center gap-2 flex-1 last:flex-none">
            <button
              type="button"
              onClick={() => onSelect(i)}
              disabled={i > reachable}
              aria-current={active ? 'step' : undefined}
              className="flex items-center gap-2 text-sm disabled:cursor-not-allowed"
            >
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  active
                    ? 'bg-primary-600 text-white'
                    : done
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
              >
                {done ? <Check size={14} aria-hidden /> : i + 1}
              </span>
              <span className={active ? 'font-semibold text-gray-900' : 'text-gray-500'}>
                {step.label}
              </span>
            </button>
            {i < CREATE_EVENT_STEPS.length - 1 && (
              <span className={`h-px flex-1 ${done ? 'bg-primary-300' : 'bg-gray-200'}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The New Event form, in three short steps: what and when (name, type as
 * cards, date with an all-day switch), where (a venue picked, or added on the
 * spot), and the rest (guests, budget, notes, and templates to start from).
 * Only the first step is required; the chosen templates are copied into the
 * event the moment it exists.
 */
export function CreateEventPanel({ onClose, onCreated }: CreateEventPanelProps) {
  const createEvent = useCreateEvent();
  const cloneTemplate = useCloneTemplateToEvent();
  const { alert } = useDialog();
  const { data: templates = [], isLoading: templatesLoading } = useTemplates();
  const [form, setForm] = useState<FormState>(initialForm);
  const [step, setStep] = useState(0);
  const [addingVenue, setAddingVenue] = useState(false);
  const [templateIds, setTemplateIds] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Until the producer ticks something themselves, the templates written for
  // the chosen event type are the default.
  const selectedTemplates = templateIds ?? suggestedTemplateIds(templates, form.eventTypeId);

  const when = buildEventWhen(form.when);
  const basicsValid = form.name.trim() !== '' && form.eventTypeId !== '' && when.ok;
  const reachable = basicsValid ? CREATE_EVENT_STEPS.length - 1 : 0;
  const stepId: StepId = CREATE_EVENT_STEPS[step].id;
  const isLast = step === CREATE_EVENT_STEPS.length - 1;

  const goTo = (index: number) => {
    setError('');
    setStep(Math.min(index, reachable));
  };

  const handleCreate = async () => {
    setError('');
    if (!when.ok) {
      setError(when.error);
      setStep(0);
      return;
    }
    setSaving(true);
    let created: Event;
    try {
      created = await createEvent.mutateAsync({
        name: form.name.trim(),
        eventTypeId: form.eventTypeId,
        startAt: when.startAt,
        ...(when.endAt && { endAt: when.endAt }),
        venueId: form.venueId || undefined,
        guestCount: form.guestCount ? parseInt(form.guestCount, 10) : undefined,
        budget: form.budget ? parseFloat(form.budget) : undefined,
        notes: form.notes.trim() || undefined,
      });
    } catch (err) {
      setSaving(false);
      setError(extractApiErrorMessage(err, 'Failed to create event'));
      return;
    }

    const chosen = templates.filter(
      (t) => isStarterTemplate(t) && selectedTemplates.includes(t.id),
    );
    const results = await Promise.allSettled(
      chosen.map((t) =>
        cloneTemplate.mutateAsync({
          templateId: t.id,
          type: t._type as 'checklist' | 'timeline' | 'kibuddim',
          eventId: created.id,
        }),
      ),
    );
    setSaving(false);
    const failed = chosen.filter((_, i) => results[i].status === 'rejected');
    if (failed.length > 0) {
      await alert({
        title: 'Event created',
        message: `The event was created, but ${failed
          .map((t) => `"${t.templateName || t.title || 'Untitled'}"`)
          .join(', ')} could not be copied in. You can add ${
          failed.length === 1 ? 'it' : 'them'
        } from the event's tabs.`,
        variant: 'warning',
      });
    }
    onCreated(created);
  };

  return (
    <div className="p-4 md:p-6 bg-white">
      <h2 className="sr-only">Create Event</h2>
      <StepIndicator current={step} reachable={reachable} onSelect={goTo} />

      {error && (
        <div role="alert" className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {stepId === 'basics' && (
        <div className="space-y-5">
          <div>
            <label htmlFor="create-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              id="create-name"
              type="text"
              value={form.name}
              autoFocus
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Cohen–Levy Wedding"
              className={input}
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">Event type</span>
            <EventTypeCards
              value={form.eventTypeId || null}
              onChange={(id) => setForm((f) => ({ ...f, eventTypeId: id }))}
            />
          </div>
          <EventWhenFields
            value={form.when}
            onChange={(next) => setForm((f) => ({ ...f, when: next }))}
          />
        </div>
      )}

      {stepId === 'venue' && (
        <div className="space-y-4">
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Venue</span>
            <VenueSelect
              value={form.venueId || null}
              onChange={(id) => setForm((f) => ({ ...f, venueId: id ?? '' }))}
              placeholder="Search venues"
              ariaLabel="Venue"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Not decided yet? Skip this step and add the venue later.
            </p>
          </div>
          {addingVenue ? (
            <QuickVenueForm
              onCancel={() => setAddingVenue(false)}
              onCreated={(venue) => {
                setForm((f) => ({ ...f, venueId: venue.id }));
                setAddingVenue(false);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingVenue(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              <Plus size={15} />
              New venue
            </button>
          )}
        </div>
      )}

      {stepId === 'details' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="create-guest-count"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Guest count
              </label>
              <input
                id="create-guest-count"
                type="number"
                value={form.guestCount}
                onChange={(e) => setForm((f) => ({ ...f, guestCount: e.target.value }))}
                placeholder="Optional"
                min="1"
                className={input}
              />
            </div>
            <div>
              <label
                htmlFor="create-budget"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Budget
              </label>
              <input
                id="create-budget"
                type="number"
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                placeholder="Optional"
                min="0"
                step="0.01"
                className={input}
              />
            </div>
          </div>
          <div>
            <label htmlFor="create-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="create-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Optional"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700">Start from templates</span>
            <p className="text-xs text-gray-500 mb-2">
              Copied into the event as soon as it is created.
            </p>
            <EventTemplatePicker
              templates={templates}
              selected={selectedTemplates}
              onChange={setTemplateIds}
              eventTypeId={form.eventTypeId}
              isLoading={templatesLoading}
            />
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => goTo(step - 1)}
            className="h-10 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <div className="flex-1" />
        {!isLast && step > 0 && (
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={!basicsValid || saving}
            className="h-10 px-4 text-sm font-medium text-primary-700 hover:text-primary-800 disabled:opacity-50"
          >
            Create now
          </button>
        )}
        {isLast ? (
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={!basicsValid || saving}
            className="h-10 px-5 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Creating…' : 'Create Event'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => goTo(step + 1)}
            disabled={!basicsValid}
            className="h-10 px-5 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}
